import { ACTIVATIONS, type Activation } from "@/lib/content";

/**
 * Time-of-day routing. Given a local hour (0-23), return the activation slug
 * whose rhythm best matches the moment. Falls back to attention-discipline.
 */
export function suggestActivationForHour(hour: number): Activation {
  const slug =
    hour >= 4 && hour < 10
      ? "rising-activation"
      : hour >= 10 && hour < 14
      ? "midday-activation"
      : hour >= 14 && hour < 18
      ? "evening-activation"
      : hour >= 18 || hour < 4
      ? "night-seal"
      : "attention-discipline";
  return ACTIVATIONS.find((a) => a.slug === slug) ?? ACTIVATIONS[0];
}

export interface CompletionRow {
  activation_slug: string;
  completed_at: string; // ISO
}

/**
 * Compute a consecutive-day streak. A day counts if it has at least one
 * completion. Streak is anchored to today (local time) or yesterday — the
 * chain doesn't break until a full day is missed.
 */
export function computeStreak(rows: CompletionRow[]): number {
  if (!rows.length) return 0;
  const days = new Set<string>();
  for (const r of rows) {
    const d = new Date(r.completed_at);
    if (Number.isNaN(d.getTime())) continue;
    days.add(dayKey(d));
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Anchor: today if completed today, else yesterday.
  let cursor = new Date(today);
  if (!days.has(dayKey(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
    if (!days.has(dayKey(cursor))) return 0;
  }

  let streak = 0;
  while (days.has(dayKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}
